import { useState } from 'react';
import {
  numToStr,
  toMillisecond,
  toSecond,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import { ISpatStorageResource } from '../../../entities/farms.entities';
import {
  Button,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  RadioButton,
  Subtitle,
} from '../../shared';
import { Modal, Radio, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { componentTranslate, translate } from '../../../lib/lang.helper';
import { selectFarmsData } from '../../../store/farms/farms.selector';
import { selectSeasons } from '../../../store/utils/utils.selector';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import { dozToPcs, pcsToDoz } from '../../../lib/common.helpers';
import { showFeedback } from '../../../store/ui/ui.actions';
import { loadSpatStorages } from '../../../store/farms/farms.actions';
import { sendSingleRequest } from '../../../apis';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import SelectBoat from '../../farmform/SelectBoat';

interface ILineForm {
  line_id: number;
  spat_amount_pcs: number | null;
  spat_amount_dz: number | null;
  basket_count: number | null;
}

interface IFormData {
  lines: ILineForm[];
  amount_unit: 'pcs' | 'dz';
  season_name: string;
  boat_id?: number | null;
  planned_date_seed: number;
  planned_date_harvest: number;
  spat_size: number | null;
  spat_size_max: number | null;
  spacing: number | null;
  comment: string | null;
}

const defaultForm: IFormData = {
  lines: [],
  season_name: '',
  boat_id: null,
  amount_unit: 'pcs',
  planned_date_seed: Date.now(),
  planned_date_harvest: Date.now(),
  spat_size: null,
  spat_size_max: null,
  spacing: null,
  comment: null,
};

interface Props {
  data?: any;
  visible: boolean;
  spatStorage: ISpatStorageResource;
  onClose: (data?: any) => void;
  overConfirm?: (data?: any) => Promise<any>;
  className?: string;
}

const OysterBulkSeedModal = ({
  data,
  visible,
  spatStorage,
  onClose,
  overConfirm,
  className,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const linesData = farmsData
    .flatMap(farm => farm.lines)
    .filter(x => x.crop_type === 'OYSTER');
  const seasons = useSelector(selectSeasons);
  const curBoat = useSelector(selectProfile)?.current_boat;
  const unitType = useSelector(selectAccount)?.display_unit;

  const [formData, setFormData] = useState<IFormData>(
    data
      ? {
          ...defaultForm,
          ...data,
          boat_id: data?.boat_id ?? curBoat?.id,
          planned_date_seed: toMillisecond(
            data?.planned_date_seed ?? defaultForm.planned_date_seed,
          ),
          planned_date_harvest: toMillisecond(
            data?.planned_date_harvest ?? defaultForm.planned_date_harvest,
          ),
          spat_size: data?.spat_size as number | null,
          spat_size_max: data?.spat_size_max as number | null,
          spacing: data?.spacing as number | null,
        }
      : {
          ...defaultForm,
          boat_id: curBoat?.id,
          spat_size: spatStorage.harvest?.shell_length as number | null,
          spat_size_max: spatStorage.harvest?.shell_length_max as number | null,
        },
  );
  const [disabled, setDisabled] = useState(false);

  const linesOption = linesData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.farm_name} (${x.farm_number}) - ${x.line_name}`,
  }));

  const selectLines = (lines: string[]) => {
    let newLines = formData.lines.filter(x =>
      lines.includes(x.line_id.toString()),
    );
    for (let idStr of lines) {
      const line = linesData.find(x => x.id === Number(idStr));
      if (!line) continue;

      const i = newLines.findIndex(x => x.line_id === line.id);
      if (i < 0) {
        newLines.push({
          line_id: line.id,
          spat_amount_dz: null,
          spat_amount_pcs: null,
          basket_count: null,
        });
      }
    }
    setFormData({ ...formData, lines: newLines });
  };
  const updateLine = (
    lineID: number,
    field: 'spat_amount_pcs' | 'spat_amount_dz' | 'basket_count',
    value: string,
  ) => {
    let newLines = [...formData.lines];
    const i = newLines.findIndex(x => x.line_id === lineID);
    if (i < 0) return;

    const val = value.length <= 0 ? null : Number(value);
    newLines[i][field] = val;

    if (val !== null) {
      if (field === 'spat_amount_dz') {
        newLines[i].spat_amount_pcs = dozToPcs(val);
      } else if (field === 'spat_amount_pcs') {
        newLines[i].spat_amount_dz = pcsToDoz(val);
      }
    }
    setFormData({ ...formData, lines: newLines });
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (['spat_size', 'spat_size_max', 'spacing'].includes(key)) {
      value = value.length > 0 ? Number(value) : null;
    } else if (key === 'comment') {
      value = value.length > 0 ? value : null;
    }
    setFormData({ ...formData, [key]: value });
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
        duration: -1,
      }),
    );
  const validFormData = () => {
    if (formData.lines.length === 0) {
      showError(translate(lang, '_sel_seeded_lines'));
      return null;
    }
    let amountSum = 0;
    for (let ln of formData.lines) {
      const line = linesData.find(x => x.id === ln.line_id);
      if (!line) continue;

      if (!ln.spat_amount_dz || !ln.spat_amount_pcs) {
        showError(
          translate(
            lang,
            'Please fill spat amount of %s - %s',
            line.farm_name,
            line.line_name,
          ),
        );
        return null;
      }
      if (!ln.basket_count) {
        showError(
          translate(
            lang,
            'Please fill %s count of %s - %s',
            line.farming_method,
            line.farm_name,
            line.line_name,
          ),
        );
        return null;
      }
      amountSum += ln.spat_amount_pcs;
    }
    if (amountSum > (spatStorage.available_weight ?? 0)) {
      showError(
        translate(
          lang,
          'Maximum amount of spat is %s kg',
          spatStorage.available_weight,
        ),
      );
      return null;
    }
    if (formData.planned_date_seed > formData.planned_date_harvest) {
      showError(translate(lang, '_seeding_date_must_before'));
      return null;
    }
    if (!formData.season_name) {
      showError(translate(lang, 'Please select season'));
      return null;
    }
    if (!formData.spat_size) {
      showError(translate(lang, '_fill_spat_size'));
      return null;
    }
    if (formData.spat_size_max && formData.spat_size_max < formData.spat_size) {
      showError(
        translate(
          lang,
          'Maximum value of spat size must be greater than minimum value',
        ),
      );
      return null;
    }
    return {
      ...formData,
      lines: formData.lines.map(x => {
        const amount =
          formData.amount_unit === 'dz' ? x.spat_amount_dz : x.spat_amount_pcs;
        const density = Number(amount) / (x.basket_count ?? 1);
        return { ...x, density, spat_amount: x.spat_amount_pcs };
      }),
      spat_storage_id: spatStorage.id,
      planned_date_seed: toSecond(formData.planned_date_seed),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
    };
  };
  const confirmClick = async () => {
    const validData = validFormData();
    if (!validData) return;

    setDisabled(true);
    if (overConfirm) {
      const r = await overConfirm(validData);
      if (r === true) {
        dispatch(loadSpatStorages());
        onClose();
      }
      return;
    }
    const res = await sendSingleRequest(
      validData,
      'POST',
      'api/farm/line/multiline-seeding',
      true,
    );
    if (res.status) {
      dispatch(loadSpatStorages());
      dispatch(
        showFeedback({
          isMessage: true,
          message: res.data?.message ?? 'Success',
          type: 'success',
          duration: 6000,
        }),
      );
      onClose(res);
    } else {
      showError(res.data?.message ?? 'Server error');
      setDisabled(false);
    }
  };
  const formLinesColumn: any = [
    {
      title: 'Farm - Line',
      key: 'line_id',
      render: (x: ILineForm) => {
        const line = linesData.find(l => l.id === x.line_id);
        return line ? `${line.farm_name} - ${line.line_name}` : '';
      },
    },
    {
      title: 'Spat amount',
      key: 'spat_amount',
      render: (x: ILineForm) => (
        <Input
          label=''
          placeholder={translate(lang, 'Spat amount')}
          type='number'
          value={numToStr(
            formData.amount_unit === 'dz'
              ? x.spat_amount_dz
              : x.spat_amount_pcs,
          )}
          onChange={e =>
            updateLine(
              x.line_id,
              formData.amount_unit === 'dz'
                ? 'spat_amount_dz'
                : 'spat_amount_pcs',
              e.target.value,
            )
          }
          unit={formData.amount_unit}
        />
      ),
    },
    {
      title: 'Number of containers',
      key: 'line_length',
      render: (x: ILineForm) => {
        const line = linesData.find(l => l.id === x.line_id);
        return (
          <Input
            label=''
            placeholder={translate(lang, 'Number of %s', line?.farming_method)}
            type='number'
            value={numToStr(x.basket_count)}
            onChange={e =>
              updateLine(x.line_id, 'basket_count', e.target.value)
            }
          />
        );
      },
      align: 'left',
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      className={className}
      closable
      closeIcon={<CloseIcon />}
      width={900}
      footer={null}
    >
      <div className='section-modal wrap'>
        <div className='mb-17'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, '_batches_seeding_lines')}
          </Subtitle>
        </div>
        <div className='section'>
          <div className='mb-17'>
            <Radio.Group
              className='d-flex'
              onChange={e => updateForm('amount_unit', e.target.value)}
              value={formData.amount_unit}
            >
              <RadioButton className='mr-7' label='Pieces' value='pcs' />
              <RadioButton className='ml-7' label='Dozens' value='dz' />
            </Radio.Group>
          </div>
          <div className='mb-17'>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Select seeded lines')}
              options={linesOption}
              value={formData.lines.map(x => x.line_id.toString()) as any}
              onChange={(v: any) => selectLines(v)}
              showSearch={true}
            />
          </div>
          <Table
            rowKey={'line_id'}
            className={'table table--units table--small'}
            pagination={false}
            columns={formLinesColumn}
            dataSource={formData.lines}
          />
          {formData.lines.length > 0 && (
            <div className='mt-7 text-right mr-17' style={{ fontSize: 16 }}>
              {componentTranslate(
                lang,
                'Total %s containers, %s %s are going to seed',
                <strong>
                  {formData.lines.reduce(
                    (p, c) => p + (c.basket_count ?? 0),
                    0,
                  )}
                </strong>,
                <strong>
                  {formData.lines.reduce(
                    (p, c) =>
                      p +
                      ((formData.amount_unit === 'dz'
                        ? c.spat_amount_dz
                        : c.spat_amount_pcs) ?? 0),
                    0,
                  )}
                </strong>,
                <span>{formData.amount_unit}</span>,
              )}
            </div>
          )}
        </div>
        <div className='section'>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <SelectBoat
                value={formData.boat_id}
                onChange={v => updateForm('boat_id', v)}
              />
            </div>
            <div className='flex-1'>
              <Dropdown
                label={translate(lang, 'Season')}
                options={seasons.map(x => ({
                  id: x.name,
                  value: x.name,
                  label: x.name,
                }))}
                value={formData.season_name}
                onChange={v => updateForm('season_name', v)}
              />
            </div>
          </div>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <Datepicker
                label={translate(lang, 'Seeding date')}
                defaultValue={formData.planned_date_seed}
                onChange={e =>
                  e && updateForm('planned_date_seed', e.toDate().getTime())
                }
              />
            </div>
            <div className='flex-1'>
              <Datepicker
                label={translate(lang, 'Planned harvest date')}
                defaultValue={formData.planned_date_harvest}
                onChange={e =>
                  e && updateForm('planned_date_harvest', e.toDate().getTime())
                }
              />
            </div>
          </div>
          <div className='d-flex justify-between-align-end mb-17 col-gap-20'>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Minimum spat size')}
                type='number'
                value={numToStr(formData.spat_size)}
                onChange={e => updateForm('spat_size', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Maximum spat size')}
                type='number'
                value={numToStr(formData.spat_size_max)}
                onChange={e => updateForm('spat_size_max', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
            <div className='flex-1'>
              <Input
                label={translate(lang, 'Basket spacing')}
                type='number'
                value={numToStr(formData.spacing)}
                onChange={e => updateForm('spacing', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
          </div>
        </div>
        <div className='mb-17 mt-17'>
          <Input
            label={translate(lang, 'Comment')}
            type='textarea'
            value={formData.comment ?? ''}
            onChange={e => updateForm('comment', e.target.value)}
          />
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width='small'
            size={2}
            type='fill'
            color='red'
            className='rsp-btn'
            onClick={onClose}
            disabled={disabled}
          >
            {translate(lang, 'Cancel')}
          </Button>
          <Button
            width='small'
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default OysterBulkSeedModal;
